import { DatePipe } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import * as moment from 'moment-timezone';

import { InsuranceType } from '../_enums/insurance-type.enum';
import {
  AnimalInsuranceMetadata,
  HealthInsuranceMetadata,
  HouseholdMetadata,
  InsuranceMetadatas,
  LegalProtectionMetadata,
  TravelMetadata,
  VehicleMetadata,
} from '../_types/lead.types';
import { COUNTRIES, LanguageCode } from './nationalities';

export const getBooleanLabel = (value?: boolean | string): string => {
  if (value !== null && value !== undefined)
    return value === true || value === 'true' ? $localize`Oui` : $localize`Non`;
  return '';
};

export const toUTCWithoutChangingTime = (dateString: string) => {
  const localDate = moment(dateString);

  // Create a new moment object in UTC with the same local time
  const utcDate = moment.utc({
    year: localDate.year(),
    month: localDate.month(),
    date: localDate.date(),
    hour: localDate.hour(),
    minute: localDate.minute(),
    second: localDate.second(),
  });

  // Handle daylight saving time (if the user in in DST and the date isn't, it will appear with a missing hour)
  const isDst = moment([
    localDate.year(),
    localDate.month(),
    localDate.date(),
  ]).isDST();

  if (!isDst && moment().isDST()) {
    utcDate.add(1, 'hour');
  }

  return utcDate;
};

export const getExtension = (docurl: string): string => {
  const myarr = docurl.split('/');
  const myimg = myarr[myarr.length - 1] ? myarr[myarr.length - 1] : '';
  const myimgg = myimg.split('.');
  return myimgg[myimgg.length - 1] ? myimgg[myimgg.length - 1] : '';
};

export const getControl = (control: AbstractControl): FormControl =>
  control as FormControl;
export const getGroup = (control: AbstractControl): FormGroup =>
  control as FormGroup;
export const getFormArray = (control: AbstractControl): FormArray =>
  control as FormArray;
export const formatDateForInput = (dateString: string): string => {
  if (!dateString) return '';
  const momentDate = moment(dateString, ['DD/MM/YYYY', 'YYYY-MM-DD']);
  return new DatePipe('fr').transform(momentDate.toDate(), 'yyyy-MM-dd')!;
};

/**
 * `yyyy-MM-dd` from local calendar parts, the value shape
 * `<input type="date">` expects.
 *
 * Deliberately not `toISOString().split('T')[0]`, which converts to UTC first
 * and so reports the previous day for anyone east of it in the evening.
 */
export const toDateInputValue = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** `yyyy-MM-ddTHH:mm`, the value shape `<input type="datetime-local">` expects. */
export const toDateTimeInputValue = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${toDateInputValue(date)}T${hours}:${minutes}`;
};

/**
 * Shifts a local `yyyy-MM-ddTHH:mm` (the `<input type="datetime-local">`
 * value) to the matching UTC wall clock, as a zone-less
 * `yyyy-MM-ddTHH:mm:ss` for a backend `LocalDateTime` that is stored in UTC.
 */
export const toUtcLocalDateTime = (localDateTime: string): string => {
  const date = new Date(localDateTime);
  if (Number.isNaN(date.getTime())) return localDateTime;
  return date.toISOString().slice(0, 19);
};

/**
 * Formats a Date as the ISO day string date-only API params expect. Uses local
 * time on purpose: toISOString() would shift a CET date back a day near midnight.
 */
export const formatDateToIsoDay = (date: Date): string =>
  moment(date).format('YYYY-MM-DD');

const toDateWithUtcFallback = (value: string | Date): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const utcString = /Z$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  const date = new Date(utcString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateToFrCh = (
  value?: string | Date | null
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = toDateWithUtcFallback(value);
  if (!date) {
    return typeof value === 'string' ? value : undefined;
  }

  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const formatDateTimeToFrCh = (
  value?: string | Date | null
): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = toDateWithUtcFallback(value);
  if (!date) {
    return typeof value === 'string' ? value : undefined;
  }

  return new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

export const customMatcherTextHelper = (
  value: string,
  filterText: string,
  filterOption: string
) => {
  switch (filterOption) {
    case 'contains':
      return value.toUpperCase().indexOf(filterText.toUpperCase()) >= 0;
    case 'notContains':
      return value.toUpperCase().indexOf(filterText.toUpperCase()) < 0;
    case 'equals':
      return value.toUpperCase() === filterText.toUpperCase();
    case 'notEqual':
      return value.toUpperCase() != filterText.toUpperCase();
    case 'startsWith':
      return value.toUpperCase().indexOf(filterText.toUpperCase()) === 0;
    case 'endsWith':
      const index = value.toUpperCase().lastIndexOf(filterText.toUpperCase());
      return index >= 0 && index === value.length - filterText.length;
    default:
      // should never happen
      return false;
  }
};

export const getInsuranceNameFromMetadata = (
  metadata: InsuranceMetadatas,
  insuranceType: InsuranceType
): string => {
  switch (insuranceType) {
    case InsuranceType.HEALTH:
      const healthMetadata = metadata as HealthInsuranceMetadata;
      return (
        healthMetadata?.chosenBaseOffer?.insuranceName ??
        healthMetadata?.chosenComplementaryOffer?.insuranceName ??
        ''
      );
    case InsuranceType.ANIMAL:
      return (
        (metadata as AnimalInsuranceMetadata)?.chosenBaseOffer?.insuranceName ??
        ''
      );
    case InsuranceType.TRAVEL:
      return (metadata as TravelMetadata)?.chosenBaseOffer?.insuranceName ?? '';
    case InsuranceType.LEGAL_PROTECTION:
      return (
        (metadata as LegalProtectionMetadata)?.chosenBaseOffer?.insuranceName ??
        ''
      );
    case InsuranceType.MOTOR_VEHICLE:
      return (
        (metadata as VehicleMetadata)?.chosenBaseOffer?.insuranceName ?? ''
      );
    case InsuranceType.RC_HOUSEHOLD:
      return (
        (metadata as HouseholdMetadata)?.chosenBaseOffer?.insuranceName ?? ''
      );
    default:
      return '';
  }
};

export const getInsuranceStartDateFromMetadata = (
  metadata: InsuranceMetadatas,
  insuranceType: InsuranceType
): string => {
  switch (insuranceType) {
    case InsuranceType.HEALTH:
      const healthMetadata = metadata as HealthInsuranceMetadata;
      return healthMetadata.startDate ?? '';
    case InsuranceType.ANIMAL:
      return (
        (metadata as AnimalInsuranceMetadata)?.insuranceStartDate?.toString() ??
        ''
      );
    case InsuranceType.TRAVEL:
      return (metadata as TravelMetadata)?.startDate ?? '';
    case InsuranceType.LEGAL_PROTECTION:
      return (metadata as LegalProtectionMetadata)?.startDate ?? '';
    case InsuranceType.MOTOR_VEHICLE:
      return (metadata as VehicleMetadata)?.insuranceStartDate ?? '';
    case InsuranceType.RC_HOUSEHOLD:
      return (metadata as HouseholdMetadata)?.startDate ?? '';
    default:
      return '';
  }
};

export const compareArrays = (
  arr1: unknown[],
  arr2: unknown[],
  comparator = (a: any, b: any) => a === b
): boolean => {
  if ((!arr1 && arr2) || (arr1 && !arr2) || arr1?.length !== arr2?.length) {
    return false;
  }

  return arr1.every((element, index) => comparator(element, arr2[index]));
};

export const compareArraysIgnoreOrder = (
  arr1: unknown[],
  arr2: unknown[],
  comparator = (a: any, b: any) => a === b
): boolean => {
  if ((!arr1 && arr2) || (arr1 && !arr2) || arr1?.length !== arr2?.length) {
    return false;
  }
  return arr1.every((element) =>
    arr2.some((element2) => comparator(element, element2))
  );
};

export const getCountryName = (
  countryCode: string,
  languageCode: LanguageCode
) => COUNTRIES[countryCode]?.[languageCode] ?? '';

export const getFileName = (docurl: string) => {
  const myarr = docurl.split('/');
  const myimg = myarr[myarr.length - 1] ? myarr[myarr.length - 1] : '';
  return myimg.split('?')[0];
};

export const noRowsOverlayMessage = $localize`Aucune donnée à afficher`;

export const isObjectEmpty = (obj: Object): boolean => {
  for (const key in obj) {
    // if the value is 'object'
    if (obj[key] instanceof Object === true) {
      if (isObjectEmpty(obj[key]) === false) return false;
    }
    // if value is string/number
    else {
      // if array or string have length is not 0.
      if (obj[key].length !== 0) return false;
    }
  }
  return true;
};

/** Placeholder shown when a value is missing. */
export const EMPTY_VALUE = '–';

export const fmtDate = (
  val: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }
): string => {
  if (!val) return EMPTY_VALUE;
  try {
    return new Date(val).toLocaleDateString(undefined, opts);
  } catch {
    return val;
  }
};

/**
 * `31.01.2026 à 14:05`, in Swiss time. A bare timestamp with no offset is
 * read as UTC, which is how the backend's history rows are serialised.
 */
export const fmtUtcDateTime = (rawDate: string | null | undefined): string => {
  if (!rawDate) return EMPTY_VALUE;
  const utcString = /Z$|[+-]\d{2}:\d{2}$/.test(rawDate)
    ? rawDate
    : `${rawDate}Z`;
  const date = new Date(utcString);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;
  const day = new Intl.DateTimeFormat('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat('fr-CH', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${day} à ${time}`;
};

const toDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? undefined : date;
};

/**
 * Formats a date range as `start – end`. The start year is shown only when it
 * differs from the end year (as a 2-digit year), keeping same-year ranges
 * compact. Returns the placeholder when either bound is missing.
 */
export const formatDatePeriod = (
  start: Date | string | null | undefined,
  end: Date | string | null | undefined
): string => {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return EMPTY_VALUE;

  const differentYears = startDate.getFullYear() !== endDate.getFullYear();
  const formattedStart = startDate.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    ...(differentYears ? { year: '2-digit' } : {}),
  });
  const formattedEnd = endDate.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `${formattedStart} – ${formattedEnd}`;
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** A picked address, split the way the address fields store it: the street alone, beside the rest. */
export type SplitAddress = {
  street: string;
  postCode: string;
  city: string;
  state: string;
  /** ISO 3166-1 alpha-2, e.g. "CH". */
  country: string;
};

/**
 * Splits a Google place into the address fields. The street is built from the route and number
 * rather than taken from the name, which is a business's name when a business was picked; the
 * city prefers what the formatted address shows, which the components sometimes lack.
 */
export const splitGoogleAddress = (
  place: google.maps.places.PlaceResult
): SplitAddress => {
  const component = (type: string) =>
    place.address_components?.find((part) => part.types.includes(type));
  const shownCity = new DOMParser()
    .parseFromString(place.adr_address ?? '', 'text/html')
    .querySelector('.locality')?.textContent;
  const street = [
    component('route')?.long_name,
    component('street_number')?.long_name,
  ]
    .filter(Boolean)
    .join(' ');
  return {
    street: street || place.name || '',
    postCode: component('postal_code')?.long_name ?? '',
    city:
      shownCity ||
      component('locality')?.long_name ||
      component('administrative_area_level_2')?.long_name ||
      '',
    state: component('administrative_area_level_1')?.long_name ?? '',
    country: component('country')?.short_name ?? '',
  };
};

export const buildSearchParams = (
  params: Record<string, string | number | boolean | string[] | undefined>
): URLSearchParams =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length) acc.append(key, value.join(','));
    } else if (value) {
      acc.append(key, String(value));
    }
    return acc;
  }, new URLSearchParams());

/** Case- and accent-insensitive, so "vaudoise" finds "Vaudoise Assurances". */
export const normalizeForSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
